<script setup lang="ts">
import type { AgentPrompt } from '../stores/agent_prompts'

// One prompt in the Prompt Library's ordered list (left pane). The row is
// a summary — label plus a one-line preview of the text — that selects the
// prompt into the editor pane on the right (PromptEditor.vue), with ↑/↓
// buttons that reorder it within its group. That order is the order the
// session composer's `/` picker offers prompts in. Reordering stays an
// immediate single action; editing happens in the editor.
defineProps<{
  prompt: AgentPrompt
  isFirst: boolean
  isLast: boolean
  selected: boolean
}>()

const emit = defineEmits<{
  select: [id: number]
  moveUp: [id: number]
  moveDown: [id: number]
}>()
</script>

<template>
  <li
    class="flex items-center gap-2 border-t border-border-divider py-2 pl-4 pr-3 sm:pl-5"
    :class="selected ? 'bg-surface-hover shadow-[inset_2px_0_0_var(--color-factory)]' : ''"
    :data-testid="`prompt-row-${prompt.id}`"
  >
    <button
      type="button"
      class="flex min-h-11 min-w-0 flex-1 cursor-pointer flex-col justify-center gap-1 rounded-lg border-0 bg-transparent p-0 text-left font-[inherit] text-ink"
      :aria-current="selected ? 'true' : undefined"
      :data-testid="`prompt-edit-${prompt.id}`"
      @click="emit('select', prompt.id)"
    >
      <span class="truncate font-medium" :data-testid="`prompt-label-display-${prompt.id}`" :title="prompt.label">{{
        prompt.label
      }}</span>
      <span class="truncate text-[13px] text-ink-muted">{{ prompt.text }}</span>
    </button>
    <button
      type="button"
      class="size-11 shrink-0 cursor-pointer rounded-lg border border-border-control bg-transparent text-ink-muted hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40 sm:size-9"
      :aria-label="`Move ${prompt.label} up`"
      :disabled="isFirst"
      :data-testid="`prompt-move-up-${prompt.id}`"
      @click="emit('moveUp', prompt.id)"
    >
      ↑
    </button>
    <button
      type="button"
      class="size-11 shrink-0 cursor-pointer rounded-lg border border-border-control bg-transparent text-ink-muted hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40 sm:size-9"
      :aria-label="`Move ${prompt.label} down`"
      :disabled="isLast"
      :data-testid="`prompt-move-down-${prompt.id}`"
      @click="emit('moveDown', prompt.id)"
    >
      ↓
    </button>
  </li>
</template>
